import {
  AccessibleContent,
  Alert,
  Button,
  Checkbox,
  CloseButton,
  CondensedButton,
  Flex,
  Grid,
  Heading,
  IconAddLine,
  IconInfoSolid,
  IconUploadLine,
  InlineList,
  Modal,
  Pill,
  RadioInput,
  RadioInputGroup,
  ScreenReaderContent,
  Spinner,
  Tag,
  Text,
  TextInput,
  ToggleDetails,
  Tooltip,
  View,
} from "@instructure/ui";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CourseLevelSelect } from "../../../Selectors/CourseLevelSelect";

import { AssessmentPlanService } from "../../../../services/AssessmentService";
import { LearningOutcomesService } from "../../../../services/LearningOutcomesService";
import { AssignmentTypeSelect } from "../../../AssignmentType/AssignmentTypeSelect";
import { LearningOutcomesTypeSelect } from "../../../LearningOutcomes/LearningOutcomesTypeSelect";
import { GoalsSingleSelect } from "../../../Selectors/GoalSelect";
import { ProgramSelect } from "../../../Selectors/ProgramSelect";
import { SubjectSelect } from "../../../Selectors/SubjectSelect";
import { TermSelect } from "../../../TermManagement/Terms/TermSelect";

import { csvParse } from "d3-dsv";
import Skeleton from "react-loading-skeleton";
import { CourseService } from "../../../../services/CourseService";
import { EmbeddedDashboardService } from "../../../../services/EmbeddedDashboardService";
import { FileType, Files } from "../../../../services/FilesService";
import { OutcomeCategoryService } from "../../../../services/OutcomeCategoryService";
import { QualitativeMeasureService } from "../../../../services/QualitativeMeasureService";
import { StructuredQuestionService } from "../../../../services/StructuredQuestionService";
import { MasteryLevelContext, TermsContext } from "../../../../utils/Context";
import { fileWriter } from "../../../../utils/FileUtils";
import { ApDataContext } from "../../../../views/Plan";
import { Card } from "../../../Card";
import {
  ERROR,
  useNotification,
} from "../../../CustomHooks/NotificationProvider";
import CanvasTermSelect from "../../../Filter/CanvasTermSelect";
import {
  getCurrentVersion,
  getVersionHistory,
  isArchived,
  sortOutcomes,
} from "../../../LearningOutcomes/utils";
import RichTextEditor, {
  rteHasContent,
} from "../../../RichTextArea/RichTextEditor";
import RichTextViewer from "../../../RichTextArea/RichTextViewer";
import { ContextSelector } from "../../../Selectors/ContextSelector";
import { ObjectiveSelect } from "../../../Selectors/ObjectiveSelect";
import RatingScaleSelect from "../../../Selectors/RatingScaleSelect";
import { MULTI, SINGLE, Selector } from "../../../Selectors/Selector";
import VarietySelector from "../../../Selectors/Variety/VarietySelector";
import AcademicYearSelect from "../../../TermManagement/AcademicYears/AcademicYearSelect";
import { sortTermsAscending } from "../../../TermManagement/utils";
import UploadFileModal from "../../Rater/UploadFileModal";
import { JADetails } from "../Details/JADetails";
import EmbeddedDashboard from "../EmbeddedDashboard";
import OfferingPreview from "../OfferingPreview";
import AssignmentRubric from "../RaterGrid/AssignmentRubric/AssignmentRubric";
import {
  CANVAS,
  EMBEDDED,
  EXTERNAL,
  QUALITATIVE,
  emailIsInList,
  emailVerification,
} from "../utils";
import QualitativeMeasureForm from "./QualitativeMeasureForm";

export const ASSIGNMENT_DESC_CHAR_COUNT = 2750;

export const MeasureForm = ({
  openModal,
  setOpenModal,
  templateFields,
  courseLevels,
  assignmentTypes,
  acSubs,
  planId,
  allCycles,
  programs,
  inTemplate,
  measure,
  prefetchedQuestions,
  onQuestionsUpdated,
}) => {
  const { t } = useTranslation();
  const { ratingScales } = useContext(MasteryLevelContext);
  const { canvasTerms, insightsTerms } = useContext(TermsContext);
  const {
    sectionIndex,
    measureIndex,
    selectedCycle,
    handleCreateMeasure,
    setSections,
    sectionId,
    extraData,
    includeGoalsObjs,
  } = useContext(ApDataContext);
  const showNotification = useNotification();
  const [assessmentType, setAssessmentType] = useState(CANVAS);

  function initializeAssignmentDesc() {
    return templateFields?.description ?? measure?.description ?? "";
  }
  const [assignmentDesc, setAssignmentDesc] = useState(
    initializeAssignmentDesc()
  );
  const [charLimitReached, setCharLimitReached] = useState(false);

  const [selectedPrograms, setSelectedPrograms] = useState([]);
  const [variety, setVariety] = useState("outcome");
  const [categories, setCategories] = useState([]);
  const [clearCategories, setClearCategories] = useState(false);
  const [selectedLos, setSelectedLos] = useState([]);
  const [los, setLos] = useState([]);
  const [loadingLos, setLoadingLos] = useState(false);

  const [typeOf, setTypeOf] = useState("targeted");
  const [mapping, setMapping] = useState("mapped_clo");

  const [selectedCollectionAcademicYears, setSelectedCollectionAcademicYears] =
    useState([]);
  const [selectedAssessingAcademicYears, setSelectedAssessingAcademicYears] =
    useState([]);
  const [selectedCollectionTerms, setSelectedCollectionTerms] = useState([]);
  const [selectedCanvasCollectionTerms, setSelectedCanvasCollectionTerms] =
    useState([]);
  const [selectedAssessingTerms, setSelectedAssessingTerms] = useState([]);

  const [selectedGoal, setSelectedGoal] = useState("");
  const [selectedObjective, setSelectedObjective] = useState("");

  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [selectedLevels, setSelectedLevels] = useState([]);
  const [selectedAssignmentType, setSelectedAssignmentType] = useState("");
  const [selectedSubmissionType, setSelectedSubmissionType] = useState(null);
  const [assignmentRubric, setAssignmentRubric] = useState(null);
  const submissionTypesStatic = {
    online_upload: t("assessment.fileUpload"),
    online_url: t("assessment.websiteURL"),
    online_text_entry: t("assessment.textEntry"),
  };
  const [loading, isLoading] = useState(false);
  const [allLos, setAllLos] = useState([]);

  const [learningOutcomeType, setLearningOutcomeType] = useState("");
  const [forProgramAssessment, setForProgramAssessment] = useState(true);
  const [editAssessment, setEditAssessment] = useState({
    subjects: [],
    course_levels: [],
    goal: {},
    objective: {},
    terms: [],
    los: [],
    programs: [],
    collecting_evidence: false,
    offerings: [],
  });
  const [loadingAssessment, setLoadingAssessment] = useState(true);
  const [triggerObjectives, setTriggerObjectives] = useState(false);

  const [clear, setClear] = useState(false);
  const [clearMastery, setClearMastery] = useState(false);
  const [clearAll, setClearAll] = useState(false);
  const [clearLoType, setClearLoType] = useState(false);

  const [assessorList, setAssessorList] = useState([]);
  const [assessorRef, setAssessorRef] = useState({ value: "" });
  const [invalidEmail, setInvalidEmail] = useState([]);
  const [uploadDialog, setUploadDialog] = useState(false);
  const [masteryMapping, setMasteryMapping] = useState("");
  const [loListForAssessment, setLoListForAssessment] = useState([]);
  const [selectedOfferings, setSelectedOfferings] = useState({});

  const measureNameRef = useRef();
  const structuredQuestionEditorRef = useRef();
  const [uploadEvidence, setUploadEvidence] = useState("no");
  const [measureName, setMeasureName] = useState("");
  const [structuredQuestions, setStructuredQuestions] = useState([]);
  const [qualEmails, setQualEmails] = useState([]);
  const [assessmentContext, setAssessmentContext] = useState("");
  const [newGuide, setNewGuide] = useState(null);
  const [fileReviewer, setFileReviewer] = useState(null);
  const [filteredCategories, setFilteredCategories] = useState([]);

  const [dashboardUrl, setDashboardUrl] = useState("");
  const [dashboardDesc, setDashboardDesc] = useState("");
  const [measureInstructions, setMeasureInstructions] = useState(
    measure?.instructions ?? ""
  );
  const [selectedDueTerm, setSelectedDueTerm] = useState(null);
  const [measureTermError, setMeasureTermError] = useState(null);
  const offeringPreviewEnabled = useMemo(() => {
    return !(!!measure && measure.deployed);
  }, []);

  const filteredOutcomes = useMemo(() => {
    return los.filter((o) => selectedLos.includes(o.id));
  }, [los, selectedLos]);

  useEffect(() => {
    if (openModal && !!templateFields) {
      setAssessmentType(templateFields.type);
      setEditAssessment((prevAssessment) => ({
        ...prevAssessment,
        collecting_evidence: templateFields.collecting_evidence ?? false,
      }));
      if (templateFields.collecting_evidence) {
        setMapping("direct_lo");
      }
    }
  }, [openModal, templateFields]);

  const checkOutcomeInVersionHistory = (existingOutcomes, outcome) => {
    const versionHistory = getVersionHistory(outcome);

    const set1 = new Set(existingOutcomes.map((item) => item.id));
    const set2 = new Set(versionHistory.map((item) => item.id));
    const intersectionValues = [...set1].filter((value) => set2.has(value));
    const intersections = existingOutcomes.filter((item) =>
      intersectionValues.includes(item.id)
    );

    return intersections;
  };

  // Fetch ILOs & ILO categories
  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    const fetchIlos = async () => {
      try {
        setLoadingLos(true);
        const promises = [
          LearningOutcomesService.getLearningOutcomesByType(
            "islo",
            null,
            signal
          ),
          OutcomeCategoryService.getOutcomeCategoriesByContext(
            "ISLO",
            true,
            signal
          ),
        ];

        const [responseLos, categories] = await Promise.all(promises);

        // Iterate through the ILOs
        for (let i = 0; i < responseLos.length; i++) {
          // Use selected outcome, even if it's not the current version
          const outcome = checkOutcomeInVersionHistory(
            editAssessment.los,
            responseLos[i]
          );
          if (outcome.length > 0) {
            responseLos[i] = outcome[0];
          } else {
            responseLos[i] = getCurrentVersion(responseLos[i]);
          }
        }
        setLos([...responseLos.filter((lo) => lo.outcome_variety === variety)]);
        setAllLos([...responseLos]);

        // Iterate through the categories
        const final_cats = [];
        categories.map((cat) => {
          if (cat.outcomes) {
            const push_obj = {
              id: cat.ddl_id,
              label: cat.name,
              outcomes: cat.outcomes,
            };
            if (
              final_cats.some((category) => category.id === push_obj.ddl_id)
            ) {
              return null;
            }
            final_cats.push(push_obj);
          }
          return null;
        });
        setCategories(final_cats);
        const filteredCats = final_cats.filter((c) =>
          c.outcomes.some(
            (o) => o.outcome_variety === variety && !isArchived(o)
          )
        );
        setFilteredCategories(filteredCats);

        setLoadingLos(false);
      } catch (e) {
        if (e.name !== "AbortError") {
          showNotification(
            t("notifications.general.fetchError", { obj: t("setup.ilosLink") }),
            ERROR
          );
        }
      }
    };

    if (learningOutcomeType === "islo") {
      fetchIlos();
    }

    return () => {
      controller.abort();
    };
  }, [learningOutcomeType]);

  // Fetch PLOs and PLO categories
  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    const fetchPlos = async () => {
      try {
        if (selectedPrograms && selectedPrograms.length > 0) {
          setLoadingLos(true);
          // Iterate through the categories
          const categoryPromises = selectedPrograms.map((program) =>
            OutcomeCategoryService.getOutcomeCategoriesByContext(
              program,
              true,
              signal
            )
          );

          let categories = await Promise.all(categoryPromises);
          categories = categories.flat();

          const final_cats = [];
          const cats = [];
          categories.forEach((category) => {
            cats.push(category);
            cats.map((cat) => {
              if (cat.outcomes) {
                const push_obj = {
                  id: cat.id,
                  label: cat.name,
                  outcomes: cat.outcomes,
                };
                if (
                  final_cats.some((category) => category.id === push_obj.id)
                ) {
                  return null;
                }
                final_cats.push(push_obj);
              }
              return null;
            });
          });
          setCategories(final_cats);
          const filteredCats = final_cats.filter((c) =>
            c.outcomes.some(
              (o) => o.outcome_variety === variety && !isArchived(o)
            )
          );
          setFilteredCategories(filteredCats);

          // Iterate through the PLOs
          let ploList = [];
          const promiseList = selectedPrograms.map((program) =>
            OutcomeCategoryService.getUncategorizedOutcomesByContext(
              program,
              signal
            )
          );

          const plos = await Promise.all(promiseList);

          plos.forEach((ploObj) => {
            ploList = ploList.concat([
              ...ploObj.level_outcomes,
              ...ploObj.program_outcomes,
            ]);
          });

          ploList = Array.from(new Set(ploList.map((lo) => lo.id))).map(
            (id) => {
              return ploList.find((lo) => lo.id === id);
            }
          );

          final_cats.forEach((cat) => {
            cat.outcomes.forEach((outcome) => {
              if (!ploList.find((o) => o.id === outcome.id)) {
                ploList.push(outcome);
              }
            });
          });
          for (let i = 0; i < ploList.length; i++) {
            // Use selected outcome, even if it's not the current version
            const outcome = checkOutcomeInVersionHistory(
              editAssessment.los,
              ploList[i]
            );
            if (outcome.length > 0) {
              ploList[i] = outcome[0];
            } else {
              ploList[i] = getCurrentVersion(ploList[i]);
            }
          }
          setLos([...ploList.filter((lo) => lo.outcome_variety === variety)]);
          setAllLos([...ploList]);
          setLoadingLos(false);
        } else {
          setLearningOutcomeType("");
          showNotification(
            t("notifications.assessments.noProgramsSelectedErrorMessage"),
            ERROR
          );
        }
      } catch (e) {
        if (e.name !== "AbortError") {
          showNotification(
            t("notifications.general.fetchError", { obj: t("setup.plosLink") }),
            ERROR
          );
        }
      }
    };

    if (learningOutcomeType === "pslo") {
      fetchPlos();
    }

    return () => {
      controller.abort();
    };
  }, [learningOutcomeType, selectedPrograms]);

  // Fetch CLOs
  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    const fetchClos = async () => {
      try {
        setLoadingLos(true);
        // change to get the offering's outcomes here
        const responseLos = await CourseService.getContextOutcomes(
          assessmentContext[0],
          null,
          null,
          true,
          null,
          signal
        );

        if (responseLos.length > 0) {
          for (let i = 0; i < responseLos.length; i++) {
            // Use selected outcome, even if it's not the current version
            const outcome = checkOutcomeInVersionHistory(
              editAssessment.los,
              responseLos[i]
            );
            if (outcome.length > 0) {
              responseLos[i] = outcome[0];
            } else {
              responseLos[i] = getCurrentVersion(responseLos[i]);
            }
          }
          setLos([...responseLos]);
          setAllLos([...responseLos]);
          setLoadingLos(false);
        } else {
          setLos([]);
          setSelectedLos([]);
          setLoadingLos(false);
        }
      } catch (e) {
        if (e.name !== "AbortError") {
          showNotification(
            t("notifications.general.fetchError", {
              obj: t("notifications.obj.clos"),
            }),
            ERROR
          );
        }
      }
    };

    if (learningOutcomeType === "cslo" && assessmentContext.length > 0) {
      fetchClos();
    }

    return () => {
      controller.abort();
    };
  }, [learningOutcomeType, assessmentContext]);

  useEffect(() => {
    if (learningOutcomeType === "") {
      setLos([]);
      setSelectedLos([]);
      setLoadingLos(false);
    }
  }, [learningOutcomeType]);

  useEffect(() => {
    if (selectedPrograms.length === 0 && forProgramAssessment) {
      setLearningOutcomeType("");
      setSelectedLos([]);
      setClearCategories(true);
    }
  }, [forProgramAssessment, selectedPrograms]);

  // Make sure verifyPlanMappings parameters are set when editing
  // a measure to avoid errors
  useEffect(() => {
    if (
      loadingAssessment &&
      !!editAssessment.id &&
      selectedLos.length > 0 &&
      selectedSubjects.length > 0 &&
      selectedLevels.length > 0
    ) {
      setLoadingAssessment(false);
    }
  }, [
    editAssessment.id,
    loadingAssessment,
    selectedLevels.length,
    selectedLos.length,
    selectedSubjects.length,
  ]);

  useEffect(() => {
    if (
      openModal &&
      !!measure &&
      (measure.type === CANVAS || measure.type === EXTERNAL)
    ) {
      setAssessmentType(
        measure.assessment_type === EXTERNAL ? EXTERNAL : CANVAS
      );
      setTypeOf(
        measure.assessment_type === "flexible" ? "flexible" : "targeted"
      );
      setMapping(measure.assessment_type);
      setLearningOutcomeType(measure.los[0].outcome_type.toLowerCase());
      if (extraData?.plan?.from_template) {
        measure.deployed = true;
      }
      setEditAssessment(measure);
      setSelectedSubjects(
        acSubs.filter((s) => measure.subjects.includes(s.id))
      );
      setSelectedLevels(measure.course_levels);

      const selectedInsightsTerms = [];
      measure.terms.forEach((term) => {
        if (!selectedInsightsTerms.includes(term.insights_term_id)) {
          selectedInsightsTerms.push(term.insights_term_id);
        }
      });
      setSelectedCollectionTerms(
        insightsTerms.filter((term) => selectedInsightsTerms.includes(term.id))
      );
      setSelectedCanvasCollectionTerms(
        measure.terms.map((term) => term.ddl_id)
      );

      // this is to account for the fact that the field
      // is sent back as an array, but when it's selected
      // it's a string
      if (measure.assessment_type === EXTERNAL) {
        const canvasTerm = canvasTerms.find(
          (t) => t.id === measure.assessing_terms[0]
        );
        if (canvasTerm) {
          setSelectedAssessingTerms(canvasTerm.insights_term_id);
        }
      } else {
        const selectedInsightsTerms = [];
        measure.assessing_terms.forEach((term) => {
          const canvasTerm = canvasTerms.find((t) => t.id === term);
          if (!selectedInsightsTerms.includes(canvasTerm.insights_term_id)) {
            selectedInsightsTerms.push(canvasTerm.insights_term_id);
          }
        });
        setSelectedAssessingTerms(
          insightsTerms.filter((term) =>
            selectedInsightsTerms.includes(term.id)
          )
        );
      }

      setSelectedPrograms(
        measure.programs && measure.programs.length > 0
          ? measure.programs.map((program) => program.ddl_id)
          : []
      );
      setForProgramAssessment(
        !!(measure.programs && measure.programs.length > 0)
      );
      setSelectedAssignmentType(measure.assignment_type);
      onGoalSelect(measure.goal?.id ? measure.goal.id : measure.goal);
      onObjectiveSelect(measure.objective?.id ? measure.objective.id : "0");
      const currentObjectives = [];
      selectedCycle.goals.forEach((goal) => {
        currentObjectives.push.apply(currentObjectives, goal.objectives);
      });
      setSelectedSubmissionType(
        measure.canvas_submission_type ? measure.canvas_submission_type : null
      );
      if (measure.assignment_rubric) {
        setAssignmentRubric(measure.assignment_rubric);
        setMasteryMapping(
          measure.assignment_rubric.rating_scales[0].rating_to_mastery_level_map
        );
      }
      if (measure.los[0].outcome_variety === "standard") {
        setVariety("standard");
      }

      const relevantOutcomes = measure.los.filter(
        (lo) => measure.deployed || !isArchived(lo)
      );
      selectLoHandler(
        relevantOutcomes.map((lo) => lo.id),
        relevantOutcomes
      );

      const offeringList = {};
      measure.offerings.forEach((o) => {
        if (o.course_summary.academic_subject.name in offeringList) {
          offeringList[o.course_summary.academic_subject.name].push(o.id);
        } else {
          offeringList[o.course_summary.academic_subject.name] = [o.id];
        }
      });
      setSelectedOfferings(offeringList);

      setAssessmentContext(
        measure.assessment_type === EXTERNAL
          ? measure.offerings.map((c) => c.id)
          : null
      );

      setAssessorList(
        measure.assessors.length > 0 ? measure.assessors.split(",") : []
      );
      setFileReviewer({ current: { value: measure.evidence_approver } });
    }
  }, [openModal, measure]);

  useEffect(() => {
    if (openModal && !!measure && measure.type === QUALITATIVE) {
      const newAssessment = {
        id: measure.ddl_id,
        subjects: [],
        course_levels: [],
        goal: {},
        objective: {},
        terms: [],
        los: [],
        programs: [],
        collecting_evidence: false,
      };
      setEditAssessment(newAssessment);
      setAssessmentType(measure.type);

      setMeasureName(measure.name);
      setUploadEvidence(measure.upload_evidence ? "yes" : "no");
      setMeasureInstructions(measure.instructions ?? "");
      onGoalSelect(measure.goal?.id ?? "");
      onObjectiveSelect(measure.objective?.id ? measure.objective.id : "0");
      setQualEmails(
        measure.assignees && measure.assignees.length > 0
          ? measure.assignees.split(",")
          : []
      );
      setSelectedDueTerm(measure.insights_term_id ?? null);
      // Use prefetched questions if available, otherwise empty
      setStructuredQuestions(prefetchedQuestions ?? []);
    }
  }, [openModal, measure]);

  useEffect(() => {
    if (openModal && !!measure && measure.type === EMBEDDED) {
      const newAssessment = {
        id: measure.ddl_id,
        subjects: [],
        course_levels: [],
        goal: {},
        objective: {},
        terms: [],
        los: [],
        programs: [],
        collecting_evidence: false,
      };
      setEditAssessment(newAssessment);
      setAssessmentType(measure.type);

      setMeasureName(measure.name);
      setDashboardDesc(measure.description);
      setDashboardUrl(measure.url);
    }
  }, [openModal, measure]);

  useEffect(() => {
    if (clearAll) {
      setClearAll(false);
    }
  }, [clearAll]);

  useEffect(() => {
    if (assessmentType === EXTERNAL) {
      setMapping("direct_lo");
    }
  }, [assessmentType]);

  const handleMasteryMapSelect = (map) => {
    setClearMastery(false);
    setMasteryMapping(map);
    setAssignmentRubric(null);
  };

  const handleCategorySelect = (categoryIds) => {
    setClearMastery(true);
    setMasteryMapping("");
    setClearCategories(false);
    if (categoryIds.length === 0) {
      setLos(allLos.filter((lo) => lo.outcome_variety === variety));
    } else {
      const selectedCategories = categories.filter((c) =>
        categoryIds.includes(c.id)
      );

      selectedCategories.forEach((cat) => {
        cat.outcomes.forEach((o, index, array) => {
          array[index] = getCurrentVersion(o);
        });
      });

      setLos(
        selectedCategories
          .map((c) => c.outcomes)
          .flat()
          .filter((lo) => lo.outcome_variety === variety)
      );
    }
    const selectedOutcomes = [];
    categoryIds.forEach((catId) => {
      const category = categories.find((c) => c.id === catId);
      selectedLos.forEach((lo) => {
        if (category.outcomes.find((o) => o.id === lo)) {
          selectedOutcomes.push(lo);
        }
      });
    });
    setSelectedLos(selectedOutcomes);
  };

  const handleVarietySelect = (variety) => {
    setClearMastery(true);
    setMasteryMapping("");
    setVariety(variety);
    setClearCategories(true);
    setSelectedLos([]);
    const filteredCats = categories.filter((c) => {
      return c.outcomes.some(
        (o) => o.outcome_variety === variety && !isArchived(o)
      );
    });
    setFilteredCategories(filteredCats);
    setLos([...allLos.filter((lo) => lo.outcome_variety === variety)]);
  };

  const clearFields = () => {
    setClearAll(true);
    setLearningOutcomeType("");
    setSelectedLos([]);
    setSelectedAssignmentType("");
    setSelectedGoal("");
    setSelectedPrograms([]);
    setSelectedObjective("");
    setSelectedSubmissionType(null);
    setSelectedSubjects([]);
    setSelectedLevels([]);
    setSelectedCollectionAcademicYears([]);
    setSelectedCollectionTerms([]);
    setSelectedCanvasCollectionTerms([]);
    setSelectedAssessingAcademicYears([]);
    setSelectedAssessingTerms([]);
    setMasteryMapping("");
    setAssignmentRubric(null);
    setCategories([]);
    setAssessorList([]);
    setAssignmentDesc(initializeAssignmentDesc());
    setCharLimitReached(false);
    setForProgramAssessment(true);
    setSelectedOfferings({});
    setAssessmentType(CANVAS);
    setMapping(assessmentType === EXTERNAL ? "direct_lo" : "mapped_clo");
    setMeasureName("");
    setQualEmails([]);
    setInvalidEmail([]);
    setAssessmentContext("");
    setNewGuide(null);
    setVariety("outcome");
    setLoadingAssessment(true);
    setMeasureInstructions("");
    setStructuredQuestions([]);
    setTypeOf("targeted");
    setSelectedDueTerm(null);
  };

  const onCourseLevelSelect = (levelsSelected) => {
    if (levelsSelected) {
      setSelectedLevels(levelsSelected);
    }
  };

  const onCollectionAcademicYearSelect = (yearsSelected) => {
    if (yearsSelected) {
      setSelectedCollectionAcademicYears(yearsSelected);
      if (selectedCanvasCollectionTerms.length > 0) {
        let includedTerms = [];
        yearsSelected.forEach((year) => {
          includedTerms = includedTerms.concat(
            year.insights_terms.map((term) => term.id)
          );
        });
        onCollectionTermSelect(
          selectedCollectionTerms
            .filter((term) => includedTerms.includes(term.id))
            .map((term) => term.id)
        );
      }
    }
  };

  const onCollectionTermSelect = (termsSelected) => {
    if (termsSelected) {
      // If the term is added, then add the corresponding canvas terms
      if (termsSelected.length > selectedCollectionTerms.length) {
        const addedTermId = termsSelected[termsSelected.length - 1];
        const addedTerm = insightsTerms.find((t) => t.id === addedTermId);
        const canvasTermIds = selectedCanvasCollectionTerms.concat(
          addedTerm.canvas_terms.map((t) => t.id)
        );
        const selectedCanvasTerms = canvasTerms.filter((t) =>
          canvasTermIds.includes(t.id)
        );
        selectedCanvasTerms.sort(
          (a, b) => new Date(a.start_at) - new Date(b.start_at)
        );
        setSelectedCanvasCollectionTerms(selectedCanvasTerms.map((t) => t.id));
      }
      // If terms are removed, then remove the corresponding canvas terms
      if (termsSelected.length < selectedCollectionTerms.length) {
        const removedTerms = selectedCollectionTerms.filter(
          (term) => !termsSelected.includes(term.id)
        );
        let removedCanvasTermIds = [];
        removedTerms.forEach((term) => {
          removedCanvasTermIds = removedCanvasTermIds.concat(
            term.canvas_terms.map((t) => t.id)
          );
        });
        const canvasTermIds = selectedCanvasCollectionTerms.filter(
          (term) => !removedCanvasTermIds.includes(term)
        );
        const selectedCanvasTerms = canvasTerms.filter((t) =>
          canvasTermIds.includes(t.id)
        );
        selectedCanvasTerms.sort(
          (a, b) => new Date(a.start_at) - new Date(b.start_at)
        );
        setSelectedCanvasCollectionTerms(selectedCanvasTerms.map((t) => t.id));
      }
      setSelectedCollectionTerms(
        insightsTerms.filter((t) => termsSelected.includes(t.id))
      );
    }
  };

  const onCanvasCollectionTermSelect = (termsSelected) => {
    if (termsSelected) {
      setSelectedCanvasCollectionTerms(termsSelected);
    }
  };

  const onAssessingAcademicYearSelect = (yearsSelected) => {
    if (yearsSelected) {
      setSelectedAssessingAcademicYears(yearsSelected);
      if (assessmentType === EXTERNAL) {
        setSelectedAssessingTerms([]);
      } else {
        let includedTerms = [];
        yearsSelected.forEach((year) => {
          includedTerms = includedTerms.concat(
            year.insights_terms.map((term) => term.id)
          );
        });
        onAssessingTermSelect(
          selectedAssessingTerms
            .filter((term) => includedTerms.includes(term.id))
            .map((term) => term.id)
        );
      }
    }
  };

  const onAssessingTermSelect = (termsSelected) => {
    if (termsSelected) {
      setSelectedAssessingTerms(
        insightsTerms.filter((t) => termsSelected.includes(t.id))
      );
    }
  };

  const onAssessmentTermSelect = (termSelected) => {
    setSelectedAssessingTerms(termSelected);
  };

  const onProgramSelect = (programsSelected) => {
    if (programsSelected) {
      setSelectedPrograms(programsSelected);
    }
  };

  const onGoalSelect = (goalSelected) => {
    setSelectedGoal(goalSelected);
    setTriggerObjectives(true);
  };

  const onObjectiveSelect = (objectiveSelected) => {
    setSelectedObjective(objectiveSelected);
  };

  const onSubjectSelect = (subjectsSelected) => {
    if (subjectsSelected) {
      setSelectedSubjects(subjectsSelected);
    }
  };

  const onExternalContextSelect = (context) => {
    setAssessmentContext([context]);
    setSelectedPrograms([]);
    setLearningOutcomeType("");
    setClearLoType(true);
    setClearCategories(true);
  };

  const onAssignmentSelect = (typeSelected) => {
    if (typeSelected) {
      setSelectedAssignmentType(typeSelected);
    }
  };

  const onSubmissionTypeSelect = (typeSelected) => {
    if (typeSelected) {
      setSelectedSubmissionType(typeSelected);
      // if the submission type is not online upload, then set collecting evidence to false
      if (typeSelected !== "online_upload") {
        setEditAssessment((prevState) => ({
          ...prevState,
          collecting_evidence: false,
        }));
      }
    }
  };

  function onLearningTypeSelect(learningType) {
    setClearLoType(false);
    setClearCategories(true);
    setLearningOutcomeType(learningType);
    setSelectedLos([]);
    setClearMastery(true);
    setMasteryMapping("");
  }

  function selectLoHandler(outcomes, outcomeObjs) {
    setSelectedLos([...outcomes]);
    const learningOutcomes = outcomeObjs
      ? outcomeObjs
      : los.filter((o) => outcomes.includes(o.id));
    setLoListForAssessment(
      learningOutcomes.map((o) => ({
        outcome_type: o.outcome_type,
        id: o.id,
        name: o.name,
      }))
    );
  }

  const handleAddAssessor = () => {
    // Checks to see if the email is valid
    if (!emailVerification(assessorRef.value)) {
      setInvalidEmail([{ text: t("assessment.invalidEmail"), type: "error" }]);
    } else if (emailIsInList(assessorList, assessorRef.value)) {
      setInvalidEmail([
        { text: t("assessment.assessorAlreadyAdded"), type: "error" },
      ]);
    } else {
      setAssessorList((prevList) => [...prevList, assessorRef.value]);
      assessorRef.value = "";
      setInvalidEmail([]);
    }
  };

  const getAssessingPanel = () => {
    return assessmentType === EXTERNAL
      ? null
      : [
          ((
            <Grid.Row rowSpacing="small" key={"assessingPanelHeading"}>
              <Grid.Col width={12}>
                <Heading as="h3" level="h5" border="bottom">
                  {t("assessment.assessing")}
                </Heading>
              </Grid.Col>
            </Grid.Row>
          ),
          (
            <Grid.Row vAlign="top" key={"assessingPanel"}>
              <Grid.Col>
                {editAssessment.deployed ? (
                  <Text>
                    <Text weight="bold">{t("global.departments")}: </Text>
                    {editAssessment.subjects.map((sub, idx) => (
                      <Pill key={idx} margin="0 x-small 0 0">
                        {acSubs.find((subject) => subject.id === sub).title}
                      </Pill>
                    ))}
                  </Text>
                ) : (
                  <SubjectSelect
                    selectHandler={onSubjectSelect}
                    selectedSubjects={selectedSubjects.map((sub) => sub.id)}
                    size="small"
                    isRequired
                  />
                )}
              </Grid.Col>
              <Grid.Col>
                {editAssessment.deployed ? (
                  <Text>
                    <Text weight="bold">{t("programs.courseLevels")}: </Text>
                    {editAssessment.course_levels.map((level, idx) => {
                      const courseLevel = courseLevels.find(
                        (clevel) => clevel.id === level
                      );
                      return (
                        courseLevel && (
                          <Pill key={idx} margin="0 x-small 0 0">{`${t(
                            "programs.courseLevel"
                          )} ${courseLevel.courseLevel} (${
                            courseLevel.range.start
                          } - ${courseLevel.range.end})`}</Pill>
                        )
                      );
                    })}
                  </Text>
                ) : (
                  <CourseLevelSelect
                    selectHandler={onCourseLevelSelect}
                    selectedLevels={
                      editAssessment
                        ? editAssessment.course_levels
                        : selectedLevels
                    }
                    size="small"
                    required
                  />
                )}
              </Grid.Col>
            </Grid.Row>
          )),
        ];
  };

  const getViaPanel = () => {
    return assessmentType === EXTERNAL || typeOf === "flexible"
      ? null
      : [
          <Grid.Row rowSpacing="small" key={"viaPanelHeading"}>
            <Grid.Col>
              <Heading as="h3" level="h5" border="bottom">
                {t("assessment.via")}
              </Heading>
            </Grid.Col>
          </Grid.Row>,
          <Grid.Row vAlign="middle" key={"viaPanel"}>
            <Grid.Col>
              {editAssessment.deployed ? (
                <Text>
                  <Text weight="bold">{t("assessment.assignmentType")}: </Text>
                  {
                    assignmentTypes.filter(
                      (assignType) =>
                        assignType.id === editAssessment.assignment_type
                    )[0].name
                  }
                </Text>
              ) : (
                <AssignmentTypeSelect
                  type={SINGLE}
                  label={t("assessment.assignmentType") + "*"}
                  selectHandler={onAssignmentSelect}
                  selectedAssignment={selectedAssignmentType}
                  clear={clearAll}
                  size="small"
                />
              )}
            </Grid.Col>
            {!editAssessment.deployed && (
              <Grid.Col width="auto"> {t("assessment.withLower")}</Grid.Col>
            )}
            <Grid.Col>
              {editAssessment.deployed ? (
                <Text>
                  <Text weight="bold">{t("assessment.submissionType")}: </Text>
                  {editAssessment.canvas_submission_type
                    ? submissionTypesStatic[
                        editAssessment.canvas_submission_type
                      ]
                    : t("assessment.noneSet")}
                </Text>
              ) : (
                <Selector
                  id="evidence-type-selector"
                  label={t("assessment.submissionType") + "*"}
                  assistiveText={t("assessment.submissionTypeSelect")}
                  selectHandler={onSubmissionTypeSelect}
                  options={
                    !editAssessment.collecting_evidence
                      ? [
                          {
                            id: "online_text_entry",
                            label: t("assessment.textEntry"),
                          },
                          {
                            id: "online_url",
                            label: t("assessment.websiteURL"),
                          },
                          // may add back in later
                          // {
                          //   id: "media",
                          //   label: "Media Recordings",
                          // },
                          {
                            id: "online_upload",
                            label: t("assessment.fileUpload"),
                          },
                        ]
                      : [
                          {
                            id: "online_upload",
                            label: t("assessment.fileUpload"),
                          },
                        ]
                  }
                  selectedItemId={selectedSubmissionType}
                  size="small"
                />
              )}
            </Grid.Col>
          </Grid.Row>,
        ];
  };

  const getDuringPanel = () => {
    return assessmentType === EXTERNAL
      ? null
      : [
          <Grid.Row rowSpacing="small" key="duringPanelHeading">
            <Grid.Col width={12}>
              <Heading as="h3" level="h5" border="bottom">
                {t("assessment.during")}
              </Heading>
            </Grid.Col>
          </Grid.Row>,
          <Grid.Row vAlign="top" key="duringPanel">
            <Grid.Col>
              <AcademicYearSelect
                id="collection-academic-year-select"
                selectHandler={onCollectionAcademicYearSelect}
                selectedAcademicYears={selectedCollectionAcademicYears.map(
                  (year) => year.id
                )}
                size="small"
              />
            </Grid.Col>
            <Grid.Col>
              <TermSelect
                id="collection-term-select"
                selectHandler={onCollectionTermSelect}
                selectedAcademicYears={selectedCollectionAcademicYears}
                label={t("assessment.collectionTerms") + "*"}
                size="small"
                selectedTerms={selectedCollectionTerms.map((term) => term.id)}
              />
            </Grid.Col>
            <Grid.Col>
              <CanvasTermSelect
                id="canvas-collection-term-select"
                selectHandler={onCanvasCollectionTermSelect}
                selectedTerms={selectedCollectionTerms}
                selectedCanvasTerms={selectedCanvasCollectionTerms}
                size="small"
                label={t("insightsTerms.canvasTerms") + "*"}
              />
            </Grid.Col>
          </Grid.Row>,
        ];
  };

  const getHowToAssessPanel = () => {
    return assessmentType === EXTERNAL || typeOf === "flexible" ? null : (
      /* How to assess based on CLO mappings or selected outcomes */
      <Grid.Row>
        <Grid.Col>
          <RadioInputGroup
            size="small"
            layout="columns"
            name="howToAssess"
            value={mapping}
            onChange={(e) => setMapping(e.target.value)}
            description={
              <ScreenReaderContent>
                {t("assessment.howToAssess")}:
              </ScreenReaderContent>
            }
            disabled={
              !!(editAssessment.deployed || editAssessment.collecting_evidence)
            }
          >
            <RadioInput value="mapped_clo" label={t("assessment.assessClos")} />
            <RadioInput
              value="direct_lo"
              label={t("assessment.assessDirectly")}
            />
          </RadioInputGroup>
        </Grid.Col>
      </Grid.Row>
    );
  };

  const getOfferingPreviewPanel = () => {
    return assessmentType === EXTERNAL || typeOf === "flexible" ? null : (
      <Grid.Row key="offeringPreviewPanel">
        <Grid.Col>
          {!editAssessment.deployed && (
            <Heading as="h3" level="h5" margin="0 0 small 0">
              {t("offeringPreview.heading")}
            </Heading>
          )}
          <OfferingPreview
            selectedLos={selectedLos}
            selectedSubjects={selectedSubjects}
            selectedLevels={selectedLevels}
            selectedPrograms={selectedPrograms}
            subjectList={acSubs}
            selectedOfferings={selectedOfferings}
            setSelectedOfferings={setSelectedOfferings}
            mapping={mapping}
            deployed={editAssessment.deployed && !offeringPreviewEnabled}
            editingAssessment={!!editAssessment.id}
          />
        </Grid.Col>
      </Grid.Row>
    );
  };

  const collectResultsForRows = () => {
    return [
      <Grid.Row rowSpacing="small" key="collectResultsForTitle">
        <Grid.Col width={12}>
          <Heading as="h3" level="h5" border="bottom">
            {t("assessment.collectResultsFor")}
          </Heading>
        </Grid.Col>
      </Grid.Row>,
      <Grid.Row vAlign="top" key="collectResultsForPanel">
        <Grid.Col>
          {editAssessment.deployed ? (
            <Text>
              {!editAssessment.programs ||
              (editAssessment.programs && editAssessment.programs.length > 0)
                ? t("assessment.institutionalAssessment")
                : t("assessment.programAssessment")}
            </Text>
          ) : (
            <RadioInputGroup
              layout="columns"
              name="assessmentType"
              defaultValue={"true"}
              description={
                <ScreenReaderContent>
                  {t("assessment.assessmentType")}
                </ScreenReaderContent>
              }
              value={(forProgramAssessment || false) + ""}
              onChange={() => {}}
              size="small"
            >
              <RadioInput
                key={0}
                value={"true"}
                label={t("assessment.programAssessment")}
                onClick={() => {
                  setClear(false);
                  setForProgramAssessment(true);
                  setLearningOutcomeType("");
                }}
              />
              <RadioInput
                key={1}
                value={"false"}
                label={t("assessment.institutionalAssessment")}
                onClick={() => {
                  setClear(true);
                  setEditAssessment((prevAssessment) => ({
                    ...prevAssessment,
                    programs: [],
                  }));
                  setSelectedPrograms([]);
                  setLearningOutcomeType("");
                  setSelectedLos([]);
                  setForProgramAssessment(false);
                }}
              />
            </RadioInputGroup>
          )}
        </Grid.Col>
      </Grid.Row>,
    ];
  };

  const withExternalAssessmentRows = () => {
    return assessmentType === EXTERNAL
      ? [
          <Grid.Row rowSpacing="small" key="withExternalAssessmentTitle">
            <Grid.Col width={12}>
              <Heading as="h3" level="h5" border="bottom">
                {t("assessment.withContext")}
              </Heading>
            </Grid.Col>
          </Grid.Row>,
          <Grid.Row key="withExternalAssessmentPanel">
            <Grid.Col>
              <SubjectSelect
                selectHandler={onSubjectSelect}
                selectedSubjects={selectedSubjects.map((sub) => sub.id)}
                size="small"
                isRequired
                isSingle
                withExternalContext
              />
            </Grid.Col>
            <Grid.Col>
              <ContextSelector
                isSingle={true}
                interaction={!!selectedSubjects && selectedSubjects.length > 0}
                departments={
                  selectedSubjects && selectedSubjects.length > 0
                    ? selectedSubjects
                    : null
                }
                selectHandler={onExternalContextSelect}
                creationMethod="manual"
                selectedContexts={
                  editAssessment && editAssessment.offerings
                    ? editAssessment.offerings.map((offering) => ({
                        id: offering.id,
                        label: offering.code,
                      }))
                    : null
                }
                isRequired
              />
            </Grid.Col>
          </Grid.Row>,
        ]
      : null;
  };

  const getModalHeading = () => {
    if (editAssessment.id) {
      if (assessmentType === CANVAS) {
        return t("assessment.editCanvasAssessment");
      } else if (assessmentType === EXTERNAL) {
        return t("assessment.editExternalAssessment");
      } else if (assessmentType === QUALITATIVE) {
        return t("assessment.editQualitativeMeasure");
      } else if (assessmentType === EMBEDDED) {
        return t("assessment.editEmbeddedDashboard");
      }
    } else {
      if (assessmentType === CANVAS) {
        return t("assessment.addCanvasAssessment");
      } else if (assessmentType === EXTERNAL) {
        return t("assessment.addExternalAssessment");
      } else if (assessmentType === QUALITATIVE) {
        return t("assessment.addQualitativeMeasure");
      } else if (assessmentType === EMBEDDED) {
        return t("assessment.addEmbeddedDashboard");
      }
    }
  };

  const modalAddAssessmentHeader = () => (
    <Modal.Header>
      <Grid hAlign="space-between" vAlign="middle">
        <Grid.Row>
          <Grid.Col>
            <Heading level="h2" as="h2">
              {getModalHeading()}
            </Heading>
          </Grid.Col>
          <Grid.Col width="auto">
            <CloseButton
              size="small"
              screenReaderLabel={t("global.close")}
              onClick={() => setOpenModal(false)}
            />
          </Grid.Col>
        </Grid.Row>
      </Grid>
    </Modal.Header>
  );

  const addAssessmentModalBody = () => (
    <>
      <View as="div" margin="0 0 medium 0">
        <Grid>
          <Grid.Row>
            <Grid.Col vAlign="middle">
              <Heading as="h3" level="h4">
                {assessmentType === CANVAS && t("assessment.canvasAssessment")}
                {assessmentType === EXTERNAL &&
                  t("assessment.externalAssessment")}
                {assessmentType === QUALITATIVE &&
                  t("assessment.qualitativeMeasure")}
                {assessmentType === EMBEDDED &&
                  t("embeddedDashboard.embeddedDashboard")}{" "}
                {t("assessment.assessmentDetails", {
                  cycle: selectedCycle.name,
                })}
              </Heading>
            </Grid.Col>
            {assessmentType === EXTERNAL && (
              <Grid.Col>
                {editAssessment.deployed ? (
                  <Text>
                    <Text weight="bold">
                      {t("assessment.collectSubmissions")}{" "}
                    </Text>
                    {editAssessment.collecting_evidence
                      ? t("dialogYesNo.yes")
                      : t("dialogYesNo.no")}
                  </Text>
                ) : (
                  <Checkbox
                    label={<Text>{t("assessment.collectSubmissions")}</Text>}
                    checked={editAssessment.collecting_evidence}
                    variant="toggle"
                    size="small"
                    onChange={(e) => {
                      if (e.target.checked) {
                        setEditAssessment({
                          ...editAssessment,
                          collecting_evidence: true,
                        });
                        setSelectedSubmissionType("online_upload");
                        setMapping("direct_lo");
                      } else {
                        setEditAssessment({
                          ...editAssessment,
                          collecting_evidence: false,
                        });
                      }
                    }}
                    disabled={!!inTemplate}
                  />
                )}
              </Grid.Col>
            )}
          </Grid.Row>
          {assessmentType === CANVAS && (
            <Grid.Row>
              <Grid.Col>
                {editAssessment.deployed ? (
                  <Text>
                    <Text weight="bold">
                      {t("assessment.collectResultsBy")}:{" "}
                    </Text>
                    {typeOf === "flexible"
                      ? t("assessment.listeningForOutcomeResults")
                      : t("assessment.pushingCourseAssignments")}
                  </Text>
                ) : (
                  <RadioInputGroup
                    name="assessmentTargeting"
                    description={
                      <Text size="small">
                        {t("assessment.collectResultsBy")}
                      </Text>
                    }
                    layout="columns"
                    onChange={(e) => {
                      if (e.target.value === "flexible") {
                        setEditAssessment({
                          ...editAssessment,
                          collecting_evidence: false,
                        });
                      }
                      setTypeOf(e.target.value);
                    }}
                    value={typeOf}
                  >
                    <RadioInput
                      label={
                        <Text size="small">
                          {t("assessment.pushingCourseAssignments")}
                        </Text>
                      }
                      value="targeted"
                    />
                    <RadioInput
                      label={
                        <Text size="small">
                          {t("assessment.listeningForOutcomeResults")}
                        </Text>
                      }
                      value="flexible"
                    />
                  </RadioInputGroup>
                )}
              </Grid.Col>
              <Grid.Col vAlign="bottom">
                {editAssessment.deployed ? (
                  <Text>
                    <Text weight="bold">
                      {t("assessment.collectSubmissions")}{" "}
                    </Text>
                    {editAssessment.collecting_evidence
                      ? t("dialogYesNo.yes")
                      : t("dialogYesNo.no")}
                  </Text>
                ) : (
                  <Checkbox
                    label={
                      <div>
                        <Text>{t("assessment.collectSubmissions")} </Text>
                        <Tooltip
                          renderTip={t("assessment.submissionTypeSupported")}
                          placement="top"
                          on={["click", "hover", "focus"]}
                          color="primary-inverse"
                        >
                          <button
                            style={{ backgroundColor: "#fff", border: "none" }}
                          >
                            <IconInfoSolid color="brand" />
                            <ScreenReaderContent>
                              {t("assessment.submissionTypeSupported")}
                            </ScreenReaderContent>
                          </button>
                        </Tooltip>
                      </div>
                    }
                    checked={editAssessment.collecting_evidence}
                    variant="toggle"
                    size="small"
                    onChange={(e) => {
                      if (e.target.checked) {
                        setEditAssessment({
                          ...editAssessment,
                          collecting_evidence: true,
                        });
                        setSelectedSubmissionType("online_upload");
                        setMapping("direct_lo");
                      } else {
                        setEditAssessment({
                          ...editAssessment,
                          collecting_evidence: false,
                        });
                      }
                    }}
                    disabled={inTemplate || typeOf === "flexible"}
                  />
                )}
              </Grid.Col>
            </Grid.Row>
          )}
        </Grid>
      </View>
      {assessmentType === QUALITATIVE && (
        <QualitativeMeasureForm
          goalSelectHandler={onGoalSelect}
          selectedCycle={selectedCycle}
          cycles={allCycles}
          selectedGoal={selectedGoal}
          clear={clearAll}
          objectiveSelectHandler={onObjectiveSelect}
          selectedObjective={selectedObjective}
          trigger={triggerObjectives || clearAll}
          setTrigger={setTriggerObjectives}
          measureNameRef={measureNameRef}
          uploadEvidence={uploadEvidence}
          setUploadEvidence={setUploadEvidence}
          measureName={measureName}
          qualEmails={qualEmails}
          setQualEmails={setQualEmails}
          instructions={measureInstructions}
          setInstructions={setMeasureInstructions}
          inTemplate={inTemplate}
          onDueTermSelect={setSelectedDueTerm}
          selectedDueTerm={selectedDueTerm}
          setMeasureTermError={setMeasureTermError}
          measureTermError={measureTermError}
          questions={structuredQuestions}
          setQuestions={setStructuredQuestions}
          structuredQuestionEditorRef={structuredQuestionEditorRef}
        />
      )}
      {assessmentType === EMBEDDED && (
        <EmbeddedDashboard
          dashNameRef={measureNameRef}
          dashName={measureName}
          dashboardUrl={dashboardUrl}
          setDashboardUrl={setDashboardUrl}
          dashboardDesc={dashboardDesc}
          setDashboardDesc={setDashboardDesc}
          inTemplate={inTemplate}
        />
      )}
      {(assessmentType === CANVAS || assessmentType === EXTERNAL) && (
        <Grid>
          {typeOf !== "flexible" && (
            <Grid.Row>
              <Grid.Col>
                {inTemplate || editAssessment.deployed ? (
                  <RichTextViewer
                    id="assignment-desc"
                    label={
                      <Text weight="bold">
                        {t("assessment.assignmentDesc")}
                      </Text>
                    }
                    value={initializeAssignmentDesc()}
                  />
                ) : (
                  <RichTextEditor
                    id="assignment-desc"
                    label={
                      <Text weight="bold" size="small">
                        {t("assessment.assignmentDesc")}
                      </Text>
                    }
                    defaultValue={assignmentDesc}
                    onChange={(value) => setAssignmentDesc(value)}
                    showTable={assessmentType === EXTERNAL}
                    showImage={assessmentType === EXTERNAL}
                    charLimit={
                      assessmentType === CANVAS
                        ? ASSIGNMENT_DESC_CHAR_COUNT
                        : null
                    }
                    onCharLimitReached={setCharLimitReached}
                  />
                )}
              </Grid.Col>
            </Grid.Row>
          )}
          {withExternalAssessmentRows()}
          {collectResultsForRows()}
          <Grid.Row vAlign="top">
            <Grid.Col>
              {editAssessment.deployed ? (
                <Text>
                  <Text weight="bold">{t("assessment.programs")}: </Text>
                  {editAssessment.programs && editAssessment.programs.length > 0
                    ? editAssessment.programs
                        .map((program) => program.degree_name)
                        .join(", ")
                    : t("global.none")}
                </Text>
              ) : (
                <ProgramSelect
                  type={MULTI}
                  selectHandler={onProgramSelect}
                  selectedPrograms={selectedPrograms}
                  size="small"
                  forProgramAssessment={forProgramAssessment}
                  clear={clear || clearAll}
                  isRequired
                />
              )}
            </Grid.Col>
            <Grid.Col>
              {editAssessment.deployed ? (
                <Text>
                  <Text weight="bold">{t("assessment.loType")}: </Text>
                  {editAssessment.los[0].outcome_type}
                </Text>
              ) : (
                <LearningOutcomesTypeSelect
                  selectHandler={onLearningTypeSelect}
                  selectedOutcomeTypeId={learningOutcomeType}
                  assessmentFlag={true}
                  forInstitutionalAssessment={!forProgramAssessment}
                  forExternalAssessment={assessmentType === EXTERNAL}
                  program={selectedPrograms}
                  trigger={clearLoType || clearAll}
                  isRequired
                  size="small"
                />
              )}
            </Grid.Col>
          </Grid.Row>
          <Grid.Row rowSpacing="small">
            <Grid.Col width={12}>
              <Heading as="h3" level="h5" border="bottom">
                {t("assessment.forMasteryOf")}
              </Heading>
            </Grid.Col>
          </Grid.Row>
          <Grid.Row vAlign="top">
            {!editAssessment.deployed && (
              <Grid.Col>
                <VarietySelector
                  selectedItemId={variety}
                  selectHandler={(v) => handleVarietySelect(v)}
                  interaction={learningOutcomeType && !loadingLos}
                />
              </Grid.Col>
            )}
            {!editAssessment.deployed && (
              <Grid.Col>
                <Selector
                  type={MULTI}
                  id="category-select"
                  label={t("curriculumMap.category")}
                  options={filteredCategories}
                  selectHandler={handleCategorySelect}
                  assistiveText={t("curriculumMap.selectCategories")}
                  interaction={
                    learningOutcomeType &&
                    learningOutcomeType !== "cslo" &&
                    !loadingLos
                  }
                  clear={clearCategories || clearAll}
                  size="small"
                />
              </Grid.Col>
            )}
            <Grid.Col>
              {editAssessment.deployed ? (
                <Text>
                  <Text weight="bold">{t("assessment.loTargets")}: </Text>
                  {sortOutcomes(editAssessment.los).map((lo, idx) =>
                    idx !== 0 ? ", " + lo.name : lo.name
                  )}
                </Text>
              ) : (
                <Selector
                  type={MULTI}
                  id="learning-outcome-multiple-select"
                  label={t("ratingScale.lo") + "*"}
                  options={sortOutcomes(los)
                    .filter((lo) => !lo.unapproved && !isArchived(lo))
                    .map((lo) => ({ id: lo.id, label: lo.name }))}
                  interaction={
                    (los.length > 0 || !!editAssessment) &&
                    !!learningOutcomeType &&
                    !loadingLos &&
                    (!editAssessment.id ||
                      mapping === "mapped_clo" ||
                      !assignmentRubric?.ddl_id)
                  }
                  selectHandler={(v) => {
                    selectLoHandler(v);
                  }}
                  selectedOptions={los.length > 0 ? selectedLos : []}
                  clear={clearAll}
                  size="small"
                />
              )}
            </Grid.Col>
          </Grid.Row>
          {getHowToAssessPanel()}
          {getDuringPanel()}
          {includeGoalsObjs && (
            <Grid.Row rowSpacing="small">
              <Grid.Col>
                <Heading as="h3" level="h5" border="bottom">
                  {t("assessment.with")}
                </Heading>
              </Grid.Col>
            </Grid.Row>
          )}
          {includeGoalsObjs && (
            <Grid.Row vAlign="top">
              <Grid.Col>
                {editAssessment.deployed ? (
                  <Text>
                    <Text weight="bold">{t("cyclesAndGoals.goal")}: </Text>
                    {editAssessment.goal.description}
                  </Text>
                ) : (
                  <GoalsSingleSelect
                    selectHandler={onGoalSelect}
                    goalsList={selectedCycle.goals}
                    selectedGoal={selectedGoal}
                    size="small"
                    clear={clearAll}
                    isRequired
                  />
                )}
              </Grid.Col>
              <Grid.Col>
                {editAssessment.deployed ? (
                  <Text>
                    <Text weight="bold">{t("cyclesAndGoals.objective")}: </Text>
                    {editAssessment.objective?.description
                      ? editAssessment.objective.description
                      : t("global.none")}
                  </Text>
                ) : (
                  <ObjectiveSelect
                    selectHandler={onObjectiveSelect}
                    selectedGoal={selectedGoal}
                    selectedCycle={selectedCycle}
                    selectedObjective={selectedObjective}
                    trigger={triggerObjectives || clearAll}
                    setTrigger={setTriggerObjectives}
                    size="small"
                  />
                )}
              </Grid.Col>
            </Grid.Row>
          )}
          {getAssessingPanel()}
          {getViaPanel()}
          {(!measure || !loadingAssessment) && getOfferingPreviewPanel()}
          {/* For Juried Assessment & External Assessment */}
          {editAssessment.collecting_evidence || assessmentType === EXTERNAL ? (
            <Grid.Row>
              <Grid.Col>
                <Heading as="h3" level="h4" margin="0 0 small 0">
                  {editAssessment.collecting_evidence
                    ? t("assessment.jaDetails")
                    : t("assessment.externalAssessmentDetails")}
                </Heading>
                <Card style={{ backgroundColor: "rgb(242, 248, 250)" }}>
                  <Flex wrap="wrap" gap="small" margin="0 0 small 0">
                    <Flex.Item size="100%">
                      <Grid>
                        <Grid.Row>
                          <Grid.Col>
                            <AcademicYearSelect
                              id="assessing-academic-year-select"
                              type={
                                assessmentType === EXTERNAL ? SINGLE : MULTI
                              }
                              selectHandler={onAssessingAcademicYearSelect}
                              selectedAcademicYears={
                                assessmentType === EXTERNAL
                                  ? selectedAssessingAcademicYears.id
                                  : selectedAssessingAcademicYears.map(
                                      (year) => year.id
                                    )
                              }
                              size="small"
                            />
                          </Grid.Col>
                          <Grid.Col>
                            <TermSelect
                              id="assessing-term-select"
                              selectHandler={
                                assessmentType === EXTERNAL
                                  ? onAssessmentTermSelect
                                  : onAssessingTermSelect
                              }
                              selectedAcademicYears={
                                selectedAssessingAcademicYears
                              }
                              label={
                                assessmentType === EXTERNAL
                                  ? t("assessment.assessmentTerm") + "*"
                                  : t("assessment.assessingTerms") + "*"
                              }
                              size="small"
                              selectedTerms={
                                assessmentType === EXTERNAL
                                  ? selectedAssessingTerms
                                  : selectedAssessingTerms.map(
                                      (term) => term.id
                                    )
                              }
                              isSingle={assessmentType === EXTERNAL}
                              noDefaultTerm={true}
                            />
                          </Grid.Col>
                        </Grid.Row>
                      </Grid>
                    </Flex.Item>
                    <Flex.Item
                      size={assessmentType !== EXTERNAL ? "100%" : null}
                      shouldGrow={assessmentType === EXTERNAL}
                    >
                      <RatingScaleSelect
                        title={t("ratingScale.selectMastery")}
                        size={"small"}
                        interaction={!editAssessment.id}
                        selectHandler={(scale) => {
                          handleMasteryMapSelect(scale);
                        }}
                        clear={clearMastery}
                        masteryMapping={masteryMapping}
                      />
                    </Flex.Item>
                    <Flex.Item width="100%">
                      <Flex wrap="wrap">
                        <Flex.Item shouldGrow size="100%">
                          <Heading as="h3" level="h5" border="bottom">
                            {t("assessment.assessors")}
                            <CondensedButton
                              size="small"
                              renderIcon={IconUploadLine}
                              margin="0 0 xxx-small small"
                              onClick={() => setUploadDialog(true)}
                            >
                              {t("assessment.uploadAssessors")}
                            </CondensedButton>
                          </Heading>
                        </Flex.Item>
                        <Flex.Item margin="small small 0 0">
                          <TextInput
                            type="email"
                            size="small"
                            placeholder={t("assessment.email")}
                            renderLabel={
                              <ScreenReaderContent>
                                {t("assessment.email")}
                              </ScreenReaderContent>
                            }
                            messages={invalidEmail}
                            inputRef={(el) => setAssessorRef(el)}
                          />
                        </Flex.Item>
                        <Flex.Item align="start" margin="small 0 0 0">
                          <Button
                            size="small"
                            renderIcon={IconAddLine}
                            onClick={handleAddAssessor}
                          >
                            {t("global.add")}
                          </Button>
                        </Flex.Item>
                        {assessorList.length > 0 && (
                          <Flex.Item size="100%">
                            <InlineList>
                              {assessorList.map((assessor, key) => (
                                <InlineList.Item key={key} margin="small 0 0 0">
                                  <Tag
                                    text={
                                      <AccessibleContent
                                        alt={t("assessment.deleteAssessor")}
                                      >
                                        {assessor}
                                      </AccessibleContent>
                                    }
                                    dismissible
                                    onClick={() =>
                                      setAssessorList(
                                        assessorList.filter(
                                          (_a, index) => index !== key
                                        )
                                      )
                                    }
                                  />
                                </InlineList.Item>
                              ))}
                            </InlineList>
                          </Flex.Item>
                        )}
                      </Flex>
                    </Flex.Item>
                  </Flex>
                  {editAssessment.collecting_evidence && (
                    <ToggleDetails
                      summary={
                        <Text size="small" weight="bold" color="brand">
                          {t("assessment.manageFileContent")}
                        </Text>
                      }
                    >
                      <JADetails
                        planId={planId}
                        assessment={editAssessment}
                        newGuide={newGuide}
                        setNewGuide={setNewGuide}
                        setFileReviewer={setFileReviewer}
                        handleSetFilesApprover={handleSetFilesApprover}
                      />
                    </ToggleDetails>
                  )}
                </Card>
              </Grid.Col>
            </Grid.Row>
          ) : (
            mapping === "direct_lo" && (
              <Grid.Row>
                <Grid.Col>
                  {editAssessment.deployed ? (
                    <Text>
                      <Text weight="bold">
                        {t("ratingScale.masteryRatingScale")}:{" "}
                      </Text>
                      {
                        ratingScales.find(
                          (scale) => scale.id === masteryMapping
                        ).title
                      }
                    </Text>
                  ) : (
                    <RatingScaleSelect
                      title={t("ratingScale.selectMastery")}
                      size={"small"}
                      interaction={!editAssessment.id}
                      selectHandler={(scale) => {
                        handleMasteryMapSelect(scale);
                      }}
                      clear={clearMastery}
                      masteryMapping={masteryMapping}
                    />
                  )}
                </Grid.Col>
              </Grid.Row>
            )
          )}
          {mapping === "direct_lo" && (
            <Grid.Row>
              <Grid.Col>
                <Heading as="h3" level="h4" margin="0 0 small 0">
                  {t("assessment.assignmentRubric")}
                </Heading>
                {selectedLos.length > 0 && masteryMapping !== "" ? (
                  los.length > 0 ? (
                    <AssignmentRubric
                      assessment={editAssessment}
                      outcomes={filteredOutcomes}
                      masteryMapping={masteryMapping}
                      deployed={editAssessment.deployed}
                      assignmentRubric={assignmentRubric}
                      setAssignmentRubric={setAssignmentRubric}
                    />
                  ) : (
                    <Skeleton height={250} />
                  )
                ) : (
                  <Alert>
                    {t("ratingScale.pleaseSelectOutcomeAndMastery")}
                  </Alert>
                )}
              </Grid.Col>
            </Grid.Row>
          )}
        </Grid>
      )}
    </>
  );
  const missingAssignmentRubric = () => {
    if (
      assignmentRubric === null ||
      assignmentRubric.rating_scales.length !== selectedLos.length
    ) {
      return true;
    } else {
      return false;
    }
  };

  const missingRequiredFields = () => {
    if (assessmentType === CANVAS && charLimitReached) {
      showNotification(
        t("notifications.assessments.charLimitExceeded", {
          limit: ASSIGNMENT_DESC_CHAR_COUNT,
        }),
        ERROR
      );
      return true;
    }
    const missing_fields = [];
    if (forProgramAssessment && selectedPrograms.length === 0) {
      missing_fields.push(t("programs.programs"));
    }
    if (learningOutcomeType === null || learningOutcomeType === "") {
      missing_fields.push(t("learningOutcomes.typeSelector"));
    }
    if (selectedLos === null || selectedLos.length === 0) {
      missing_fields.push(t("ratingScale.lo"));
    }
    if (
      includeGoalsObjs &&
      (selectedGoal === undefined ||
        selectedGoal.length === undefined ||
        selectedGoal.length < 1)
    ) {
      missing_fields.push(t("cyclesAndGoals.goal"));
    }
    if (selectedSubjects.length === 0) {
      missing_fields.push(t("global.departments"));
    }
    if (selectedLevels.length === 0) {
      missing_fields.push(t("programs.courseLevels"));
    }
    if (
      typeOf !== "flexible" &&
      (selectedAssignmentType === null || selectedAssignmentType === "")
    ) {
      missing_fields.push(t("assessment.assignmentType"));
    }
    if (
      typeOf !== "flexible" &&
      (selectedSubmissionType === null || selectedSubmissionType === "")
    ) {
      missing_fields.push(t("assessment.submissionType"));
    }
    if (selectedCanvasCollectionTerms.length === 0) {
      missing_fields.push(t("assessment.collectionTerms"));
    }
    if (editAssessment.collecting_evidence && assessorList.length === 0) {
      missing_fields.push(t("assessment.assessors"));
    }
    if (missing_fields.length === 0) {
      if (mapping === "direct_lo" && missingAssignmentRubric()) {
        showNotification(
          t("notifications.assessments.missingAssignmentRubric"),
          ERROR
        );
        return true;
      }
      return false;
    }
    let alert_message = "";
    const num_missing_fields = missing_fields.length;
    if (num_missing_fields === 1) {
      alert_message +=
        missing_fields[0] +
        t("notifications.assessments.missingMultipleFieldsErrorMessage");
    } else {
      missing_fields.forEach((field, idx) => {
        if (idx === num_missing_fields - 1) {
          alert_message +=
            t("assessment.and") +
            " " +
            field +
            t("notifications.assessments.missingSingleFieldErrorMessage");
        } else if (num_missing_fields === 2) {
          alert_message += field + " ";
        } else {
          alert_message += field + ", ";
        }
      });
    }
    showNotification(alert_message, ERROR);
    return true;
  };

  async function upsertAssessmentGuide(assessmentId) {
    try {
      if (newGuide) {
        await Files.saveFile(
          newGuide,
          FileType.ASSESSMENT_GUIDES,
          assessmentId
        );
        setNewGuide(null);
      }
    } catch {
      showNotification(t("notifications.guide.uploadError"), ERROR);
    }
  }

  async function upsertAssignmentDescription(assessmentId, description) {
    try {
      if (typeOf !== "flexible" && rteHasContent(description)) {
        const descriptionFile = fileWriter(
          description,
          "response.html",
          "text/html"
        );
        await Files.saveFile(
          descriptionFile,
          "assignmentDescription",
          assessmentId
        );
      } else {
        await Files.deleteFile(
          "response.html",
          "assignmentDescription",
          assessmentId
        );
      }
    } catch {
      showNotification(
        t("notifications.general.updateError", {
          obj: t("assessment.assignmentDesc"),
        }),
        ERROR
      );
    }
  }

  const handleSetFilesApprover = async (email, planId, assessmentId) => {
    let success = true;
    await AssessmentPlanService.updateAssessmentEvidenceApprover(
      email,
      planId,
      assessmentId
    )
      .then((res) => {
        setSections((prevSections) => {
          const newSections = [...prevSections];
          newSections[sectionIndex].measures[measureIndex].evidence_approver =
            res.evidence_approver;
          return newSections;
        });
      })
      .catch(async (e) => {
        success = false;
        const res = await e.json();
        if (res === t("notifications.evidenceApproval.userNotFound")) {
          showNotification(
            t("notifications.evidenceApproval.userNotFound"),
            ERROR
          );
        } else if (
          res === t("notifications.evidenceApproval.userNoPermission")
        ) {
          showNotification(
            t("notifications.evidenceApproval.userNoPermission"),
            ERROR
          );
        } else {
          showNotification(t("notifications.evidenceApproval.error"), ERROR);
        }
      });
    return success;
  };

  const handleAssessmentVerification = () => {
    if (missingRequiredFields()) {
      return;
    }
    isLoading(true);
    const termVals = [];
    canvasTerms.forEach((term) => {
      if (selectedCanvasCollectionTerms.includes(term.id)) {
        termVals.push({ id: term.id, name: term.name });
      }
    });
    const earliestAssessingCanvasTerms = [];
    selectedAssessingTerms.forEach((insightsTerm) => {
      const canvasTerms = insightsTerm.canvas_terms.sort(
        (a, b) => new Date(a.start_at) - new Date(b.start_at)
      );
      earliestAssessingCanvasTerms.push(canvasTerms[0].id);
    });
    const programVals = [];
    selectedPrograms.forEach((programId) => {
      const program = programs.find((p) => p.id === programId);
      programVals.push({
        id: programId,
        degree_name: program.degreeName,
        program_type: program.programType,
      });
    });

    let targetedOutcomes = [...loListForAssessment];
    if (assignmentRubric) {
      targetedOutcomes = targetedOutcomes.map((lo) => ({
        ...lo,
        rating_scale: assignmentRubric.rating_scales.find(
          (rs) => rs.criteria_id === lo.id
        ).ddl_id,
      }));
    }
    const noObjective =
      !includeGoalsObjs || !selectedObjective || selectedObjective === "0";

    const assessmentAdd = !editAssessment.id
      ? {
          los: targetedOutcomes,
          assessment_type: typeOf === "flexible" ? "flexible" : mapping,
          deployed: false,
          subjects: selectedSubjects.map((sub) => sub.id),
          course_levels: selectedLevels,
          terms: termVals,
          goal: !includeGoalsObjs ? null : { id: selectedGoal },
          objective: noObjective ? null : { id: selectedObjective },
          assignment_type:
            mapping !== "flexible" ? selectedAssignmentType : null,
          canvas_submission_type:
            mapping !== "flexible" ? selectedSubmissionType : null,
          collecting_evidence: editAssessment.collecting_evidence,
          programs: programVals,
          purl_type: editAssessment.collecting_evidence
            ? "juried"
            : "non_juried",
          assessing_terms: earliestAssessingCanvasTerms,
          assessing_outcomes: selectedLos,
          assessors: assessorList.join(","),
          offerings:
            typeOf === "flexible"
              ? []
              : Object.values(selectedOfferings).flat(),
          sequence: measureIndex,
        }
      : {
          id: editAssessment.id,
          los: targetedOutcomes,
          assessment_type: typeOf === "flexible" ? "flexible" : mapping,
          deployed: editAssessment.deployed,
          subjects:
            selectedSubjects &&
            selectedSubjects.length > 0 &&
            typeof selectedSubjects[0] === "string"
              ? selectedSubjects
              : selectedSubjects.map((sub) => sub.id),
          course_levels: selectedLevels,
          terms: termVals,
          goal: !includeGoalsObjs ? null : { id: selectedGoal },
          objective: noObjective ? null : { id: selectedObjective },
          assignment_type:
            mapping !== "flexible" ? selectedAssignmentType : null,
          canvas_submission_type:
            mapping !== "flexible" ? selectedSubmissionType : null,
          collecting_evidence: editAssessment.collecting_evidence,
          programs: programVals,
          purl_type: editAssessment.collecting_evidence
            ? "juried"
            : "non_juried",
          assessing_terms: earliestAssessingCanvasTerms,
          assessing_outcomes: selectedLos,
          assessors: assessorList.join(","),
          offerings:
            typeOf !== "flexible"
              ? Object.values(selectedOfferings).flat()
              : [],
          sequence: measureIndex,
        };
    const assessment = {
      planId: planId ?? null,
      sectionId: sectionId ?? null,
      assessment: assessmentAdd,
      rubricId: assignmentRubric?.ddl_id ?? null,
    };
    if (editAssessment.id) {
      AssessmentPlanService.updateAssessment(assessment)
        .then(async (newAssessment) => {
          await upsertAssessmentGuide(newAssessment.id);
          await upsertAssignmentDescription(newAssessment.id, assignmentDesc);
          return newAssessment;
        })
        .then((updatedAssessment) => {
          updatedAssessment = {
            ...updatedAssessment,
            type: CANVAS,
            description: assignmentDesc,
          };
          handleCreateMeasure(updatedAssessment, sectionIndex, measureIndex);
          setOpenModal(false);
        })
        .catch(() => {
          showNotification(
            t("notifications.assessments.createAssessmentError"),
            ERROR
          );
        })
        .finally(() => isLoading(false));
    } else {
      AssessmentPlanService.createAssessment(assessment)
        .then(async (newAssessment) => {
          await upsertAssessmentGuide(newAssessment.id);
          await upsertAssignmentDescription(newAssessment.id, assignmentDesc);
          return newAssessment;
        })
        .then((newAssessment) => {
          newAssessment = {
            ...newAssessment,
            type: CANVAS,
            description: assignmentDesc,
          };
          handleCreateMeasure(newAssessment, sectionIndex, measureIndex);
          setOpenModal(false);
        })
        .catch(() => {
          showNotification(
            t("notifications.assessments.createAssessmentError"),
            ERROR
          );
        })
        .finally(() => isLoading(false));
    }
  };

  const missingRequiredQualitativeFields = () => {
    const missing_fields = [];
    if (!measureNameRef.current.value) {
      missing_fields.push(t("measures.name"));
    }
    if (
      includeGoalsObjs &&
      (selectedGoal === undefined ||
        selectedGoal.length === undefined ||
        selectedGoal.length < 1)
    ) {
      missing_fields.push(t("cyclesAndGoals.goal"));
    }
    if (qualEmails.length === 0) {
      missing_fields.push(t("assessment.email"));
    }
    if (missing_fields.length === 0) {
      return false;
    }
    let alert_message = "";
    const num_missing_fields = missing_fields.length;
    if (num_missing_fields === 1) {
      alert_message +=
        missing_fields[0] +
        t("notifications.assessments.missingMultipleFieldsErrorMessage");
    } else {
      missing_fields.forEach((field, idx) => {
        if (idx === num_missing_fields - 1) {
          alert_message +=
            t("assessment.and") +
            " " +
            field +
            t("notifications.assessments.missingSingleFieldErrorMessage");
        } else if (num_missing_fields === 2) {
          alert_message += field + " ";
        } else {
          alert_message += field + ", ";
        }
      });
    }
    showNotification(alert_message, ERROR);
    return true;
  };

  const missingRequiredExternalAssessmentFields = () => {
    const missing_fields = [];
    if (forProgramAssessment && selectedPrograms.length === 0) {
      missing_fields.push(t("programs.programs"));
    }
    if (learningOutcomeType === null || learningOutcomeType === "") {
      missing_fields.push(t("learningOutcomes.typeSelector"));
    }

    if (selectedLos === null || selectedLos.length === 0) {
      missing_fields.push(t("ratingScale.lo"));
    }
    if (
      includeGoalsObjs &&
      (selectedGoal === undefined ||
        selectedGoal.length === undefined ||
        selectedGoal.length < 1)
    ) {
      missing_fields.push(t("cyclesAndGoals.goal"));
    }
    if (selectedSubjects.length === 0) {
      missing_fields.push(t("global.departments"));
    }
    if (assessmentContext.length === 0) {
      missing_fields.push(t("assessment.assessmentExternalContext"));
    }
    if (selectedAssessingTerms.length === 0) {
      missing_fields.push(t("assessment.assessmentTerm"));
    }
    if (assessorList.length === 0) {
      missing_fields.push(t("assessment.assessors"));
    }
    if (missing_fields.length === 0) {
      if (missingAssignmentRubric()) {
        showNotification(
          t("notifications.assessments.missingAssignmentRubric"),
          ERROR
        );
        return true;
      }
      return false;
    }
    let alert_message = "";
    const num_missing_fields = missing_fields.length;
    if (num_missing_fields === 1) {
      alert_message +=
        missing_fields[0] +
        t("notifications.assessments.missingMultipleFieldsErrorMessage");
    } else {
      missing_fields.forEach((field, idx) => {
        if (idx === num_missing_fields - 1) {
          alert_message +=
            t("assessment.and") +
            " " +
            field +
            t("notifications.assessments.missingSingleFieldErrorMessage");
        } else if (num_missing_fields === 2) {
          alert_message += field + " ";
        } else {
          alert_message += field + ", ";
        }
      });
    }
    showNotification(alert_message, ERROR);
    return true;
  };

  const missingRequiredEmbeddedFields = () => {
    if (measureNameRef.current.value === "") {
      showNotification(
        t("assessment.planRequirements.embeddedNameRequired"),
        ERROR
      );
      return true;
    }
    if (dashboardDesc === "") {
      showNotification(
        t("assessment.planRequirements.embeddedDescriptionRequired"),
        ERROR
      );
      return true;
    }
    return false;
  };

  const handleEmbeddedVerification = () => {
    if (missingRequiredEmbeddedFields()) {
      return;
    }
    isLoading(true);
    const dashboardAdd = !editAssessment.id
      ? {
          name: measureNameRef.current.value,
          url: dashboardUrl,
          description: dashboardDesc,
          sequence: measureIndex,
        }
      : {
          id: editAssessment.id,
          name: measureNameRef.current.value,
          url: dashboardUrl,
          description: dashboardDesc,
          sequence: measureIndex,
        };
    const dashboard = {
      planId: planId ?? null,
      sectionId: sectionId ?? null,
      embeddedDatalink: dashboardAdd,
    };
    if (editAssessment.id) {
      EmbeddedDashboardService.updateEmbeddedDashboard(dashboard)
        .then((updatedMeasure) => {
          updatedMeasure = { ...updatedMeasure, type: EMBEDDED };
          handleCreateMeasure(updatedMeasure, sectionIndex, measureIndex);
          isLoading(false);
          setOpenModal(false);
        })
        .catch(() => {
          showNotification(
            t("notifications.assessments.createAssessmentError"),
            ERROR
          );
          isLoading(false);
        });
    } else {
      EmbeddedDashboardService.createEmbeddedDashboard(dashboard)
        .then((newMeasure) => {
          newMeasure = { ...newMeasure, type: EMBEDDED };
          handleCreateMeasure(newMeasure, sectionIndex, measureIndex);
          isLoading(false);
          setOpenModal(false);
        })
        .catch(() => {
          showNotification(
            t("notifications.assessments.createAssessmentError"),
            ERROR
          );
          isLoading(false);
        });
    }
  };

  const handleQualitativeVerification = () => {
    if (missingRequiredQualitativeFields()) {
      return;
    }
    // Validate structured questions (empty question text)
    if (
      structuredQuestions.length > 0 &&
      structuredQuestionEditorRef.current &&
      !structuredQuestionEditorRef.current.validate()
    ) {
      return;
    }
    isLoading(true);
    const goal = !includeGoalsObjs
      ? null
      : selectedCycle.goals.find((g) => g.id === selectedGoal);
    const objective =
      !includeGoalsObjs || !goal
        ? null
        : goal.objectives.find((o) => o.id === selectedObjective);

    const noObjective =
      !includeGoalsObjs || !selectedObjective || selectedObjective === "0";

    const measureAdd = !editAssessment.id
      ? {
          name: measureNameRef.current.value,
          upload_evidence: uploadEvidence === "yes",
          goal: goal
            ? { id: selectedGoal, description: goal.description }
            : null,
          objective: noObjective
            ? null
            : { id: selectedObjective, description: objective.description },
          assignees: [...qualEmails].sort().join(","),
          sequence: measureIndex,
          insights_term_id: selectedDueTerm ?? null,
        }
      : {
          id: editAssessment.id,
          name: measureNameRef.current.value,
          upload_evidence: uploadEvidence === "yes",
          goal: goal
            ? { id: selectedGoal, description: goal.description }
            : null,
          objective: noObjective
            ? null
            : { id: selectedObjective, description: objective.description },
          assignees: [...qualEmails].sort().join(","),
          sequence: measureIndex,
          insights_term_id: selectedDueTerm ?? null,
        };
    const measure = {
      planId: planId ?? null,
      sectionId: sectionId ?? null,
      qualitativeMeasure: measureAdd,
    };
    if (editAssessment.id) {
      editAssessment.instruction = measureInstructions;
      QualitativeMeasureService.updateMeasure(measure)
        .then(async (updatedMeasure) => {
          const instructionsFile = fileWriter(
            measureInstructions,
            "response.html",
            "text/html"
          );
          await Files.saveFile(
            instructionsFile,
            "qualitativeMeasure",
            updatedMeasure.ddl_id
          );
          await StructuredQuestionService.upsertStructuredQuestions(
            updatedMeasure.ddl_id,
            structuredQuestions
          );
          if (onQuestionsUpdated) onQuestionsUpdated(structuredQuestions);
          updatedMeasure = {
            ...updatedMeasure,
            type: QUALITATIVE,
            instructions: measureInstructions,
          };
          handleCreateMeasure(updatedMeasure, sectionIndex, measureIndex);
          isLoading(false);
          setOpenModal(false);
        })
        .catch(() => {
          showNotification(
            t("notifications.assessments.createAssessmentError"),
            ERROR
          );
          isLoading(false);
        });
    } else {
      QualitativeMeasureService.createMeasure(measure)
        .then(async (newMeasure) => {
          const instructionsFile = fileWriter(
            measureInstructions,
            "response.html",
            "text/html"
          );
          await Files.saveFile(
            instructionsFile,
            "qualitativeMeasure",
            newMeasure.ddl_id
          );
          if (structuredQuestions.length > 0) {
            await StructuredQuestionService.upsertStructuredQuestions(
              newMeasure.ddl_id,
              structuredQuestions
            );
          }
          if (onQuestionsUpdated) onQuestionsUpdated(structuredQuestions);
          newMeasure = {
            ...newMeasure,
            type: QUALITATIVE,
            instructions: measureInstructions,
          };
          handleCreateMeasure(newMeasure, sectionIndex, measureIndex);
          isLoading(false);
          setOpenModal(false);
        })
        .catch(() => {
          showNotification(
            t("notifications.assessments.createAssessmentError"),
            ERROR
          );
          isLoading(false);
        });
    }
  };

  const handleExternalAssessmentVerification = async () => {
    if (missingRequiredExternalAssessmentFields()) {
      return;
    }
    isLoading(true);
    const programVals = [];
    selectedPrograms.forEach((programId) => {
      const program = programs.find((p) => p.id === programId);
      programVals.push({
        id: programId,
        degree_name: program.degreeName,
        program_type: program.programType,
      });
    });

    const assessingInsightsTerm = insightsTerms.find(
      (term) => term.id === selectedAssessingTerms
    );
    const canvasTerms = sortTermsAscending(assessingInsightsTerm.canvas_terms);
    const selectedCanvasTerm = canvasTerms.find((term) => !!term.start_at);

    const noObjective =
      !includeGoalsObjs || !selectedObjective || selectedObjective === "0";

    let targetedOutcomes = [...loListForAssessment];
    if (assignmentRubric) {
      targetedOutcomes = targetedOutcomes.map((lo) => ({
        ...lo,
        rating_scale: assignmentRubric.rating_scales.find(
          (rs) => rs.criteria_id === lo.id
        ).ddl_id,
      }));
    }

    const assessmentAdd = !editAssessment.id
      ? {
          los: targetedOutcomes,
          assessment_type: EXTERNAL,
          deployed: false,
          subjects: selectedSubjects.map((sub) => sub.id),
          goal: !includeGoalsObjs ? null : { id: selectedGoal },
          objective: noObjective ? null : { id: selectedObjective },
          collecting_evidence: editAssessment.collecting_evidence,
          programs: programVals,
          purl_type: editAssessment.collecting_evidence
            ? "juried"
            : "external_assessment",
          assessing_terms: [selectedCanvasTerm.id],
          assessing_outcomes: selectedLos,
          assessors: assessorList.join(","),
          offerings: assessmentContext,
          sequence: measureIndex,
        }
      : {
          id: editAssessment.id,
          los: targetedOutcomes,
          assessment_type: EXTERNAL,
          deployed: editAssessment.deployed,
          subjects:
            selectedSubjects &&
            selectedSubjects.length > 0 &&
            typeof selectedSubjects[0] === "string"
              ? selectedSubjects
              : selectedSubjects.map((sub) => sub.id),
          goal: !includeGoalsObjs ? null : { id: selectedGoal },
          objective: noObjective ? null : { id: selectedObjective },
          collecting_evidence: editAssessment.collecting_evidence,
          programs: programVals,
          purl_type: editAssessment.collecting_evidence
            ? "juried"
            : "external_assessment",
          assessing_terms: [selectedCanvasTerm.id],
          assessing_outcomes: selectedLos,
          assessors: assessorList.join(","),
          offerings: assessmentContext,
          evidence_approver: fileReviewer?.current?.value,
          sequence: measureIndex,
        };
    const assessment = {
      planId: planId ?? null,
      sectionId: sectionId ?? null,
      assessment: assessmentAdd,
      rubricId: assignmentRubric.ddl_id,
    };

    if (editAssessment.id) {
      let approverSuccess = true;
      if (fileReviewer?.current?.value) {
        approverSuccess = await handleSetFilesApprover(
          fileReviewer.current.value,
          planId,
          editAssessment.id
        );
      }
      if (approverSuccess) {
        AssessmentPlanService.updateAssessment(assessment)
          .then(async (newAssessment) => {
            await upsertAssessmentGuide(newAssessment.id);
            await upsertAssignmentDescription(newAssessment.id, assignmentDesc);
            return newAssessment;
          })
          .then((updatedAssessment) => {
            updatedAssessment = {
              ...updatedAssessment,
              type: EXTERNAL,
              description: assignmentDesc,
            };
            handleCreateMeasure(updatedAssessment, sectionIndex, measureIndex);
            isLoading(false);
            setOpenModal(false);
          })
          .catch(() => {
            showNotification(
              t("notifications.assessments.createAssessmentError"),
              ERROR
            );
            isLoading(false);
          });
      } else {
        isLoading(false);
      }
    } else {
      AssessmentPlanService.createAssessment(assessment)
        .then(async (newAssessment) => {
          await upsertAssessmentGuide(newAssessment.id);
          await upsertAssignmentDescription(newAssessment.id, assignmentDesc);
          return newAssessment;
        })
        .then((newAssessment) => {
          newAssessment = {
            ...newAssessment,
            type: EXTERNAL,
            description: assignmentDesc,
          };
          handleCreateMeasure(newAssessment, sectionIndex, measureIndex);
          isLoading(false);
          setOpenModal(false);
        })
        .catch(() => {
          showNotification(
            t("notifications.assessments.createAssessmentError"),
            ERROR
          );
          isLoading(false);
        });
    }
  };

  const handleAddAssessment = () => {
    if (assessmentType === CANVAS) {
      handleAssessmentVerification();
    } else if (assessmentType === QUALITATIVE) {
      handleQualitativeVerification();
    } else if (assessmentType === EXTERNAL) {
      handleExternalAssessmentVerification();
    } else if (assessmentType === EMBEDDED) {
      handleEmbeddedVerification();
    }
  };

  const handleSubmitFileUpload = (assessorFile) => {
    const reader = new FileReader();
    reader.onload = function (e) {
      const tempVal = csvParse(e.target.result);
      const processedval = tempVal.map((assessor) => {
        return assessor.email;
      });
      setAssessorList(assessorList.concat(processedval));
    };
    reader.readAsText(assessorFile);
    setUploadDialog(false);
  };

  const getAddFooterText = () => {
    if (assessmentType === CANVAS || assessmentType === EXTERNAL) {
      return t("assessment.addAssessment");
    } else if (assessmentType === QUALITATIVE) {
      return t("measures.addMeasure");
    } else if (assessmentType === EMBEDDED) {
      return t("assessment.addDashboard");
    }
  };

  const getUpdateFooterText = () => {
    if (assessmentType === CANVAS || assessmentType === EXTERNAL) {
      return t("assessment.updateAssessment");
    } else if (assessmentType === QUALITATIVE) {
      return t("assessment.updateMeasure");
    } else if (assessmentType === EMBEDDED) {
      return t("assessment.updateDashboard");
    }
  };

  const modalAddAssessmentFooter = () => (
    <Modal.Footer>
      <Button
        margin="0 small 0 0"
        onClick={() => {
          setUploadDialog(false);
          setOpenModal(false);
        }}
      >
        {t("global.cancel")}
      </Button>
      {editAssessment.deployed || editAssessment.id ? (
        <Button
          color="primary"
          onClick={handleAddAssessment}
          interaction={
            !loading &&
            !measureTermError &&
            (Object.values(selectedOfferings).flat().length > 0 ||
              typeOf === "flexible" ||
              assessmentType === QUALITATIVE ||
              assessmentType === EMBEDDED ||
              assessmentType === EXTERNAL)
              ? "enabled"
              : "disabled"
          }
        >
          {loading ? (
            <Spinner renderTitle={t("global.pleaseWait")} size="x-small" />
          ) : (
            getUpdateFooterText()
          )}
        </Button>
      ) : (
        <Button
          color="primary"
          onClick={handleAddAssessment}
          interaction={
            !loading &&
            !measureTermError &&
            (Object.values(selectedOfferings).flat().length > 0 ||
              typeOf === "flexible" ||
              assessmentType === QUALITATIVE ||
              assessmentType === EMBEDDED ||
              assessmentType === EXTERNAL)
              ? "enabled"
              : "disabled"
          }
        >
          {loading ? (
            <Spinner renderTitle={t("global.pleaseWait")} size="x-small" />
          ) : (
            getAddFooterText()
          )}
        </Button>
      )}
    </Modal.Footer>
  );
  return (
    <>
      <Modal
        id="add-assessment-plan-modal"
        open={openModal}
        size="large"
        label={getModalHeading()}
        onExited={clearFields}
      >
        {modalAddAssessmentHeader()}
        <Modal.Body>{addAssessmentModalBody()}</Modal.Body>
        {modalAddAssessmentFooter()}
      </Modal>

      <UploadFileModal
        fileExtensions={".csv"}
        shouldOpen={uploadDialog}
        modalHeaderText={t("assessment.uploadAssessors")}
        fileDropHeader={t("assessment.dropAssessors")}
        samplePath={"/sampleAssessors.csv"}
        handleCancel={() => setUploadDialog(false)}
        handleSubmit={handleSubmitFileUpload}
        modalCopy={
          <>
            <View as="div" margin="small 0">
              <Text as="p" size="small">
                {t("assessment.uploadAssessorDesc")}
              </Text>
            </View>
            <View as="div" margin="small 0">
              <Text as="p" size="small">
                {t("demographics.demographicsCopyLine2")}
              </Text>
            </View>
          </>
        }
      />
    </>
  );
};
